export const CALENDAR_COLORS = [
  '#1a73e8',
  '#9334e6',
  '#d50000',
  '#e67c73',
  '#f4511e',
  '#f6bf26',
  '#33b679',
  '#039be5',
  '#7986cb',
  '#616161',
];

/** Cor amber-400 — mesma do indicador de atualizações no menu superior. */
export const TASKS_CALENDAR_COLOR = '#fbbf24';

/** Tarefas canceladas no calendário de tarefas. */
export const TASKS_CALENDAR_CANCELLED_COLOR = '#9ca3af';

/** Tarefas concluídas, quando o calendário está configurado para exibi-las. */
export const TASKS_CALENDAR_COMPLETED_COLOR = '#059669';

export function getTasksCalendarEventColor(status?: string | null): string {
  if (status === 'Cancelled') return TASKS_CALENDAR_CANCELLED_COLOR;
  if (status === 'Completed') return TASKS_CALENDAR_COMPLETED_COLOR;
  return TASKS_CALENDAR_COLOR;
}

/** A agenda plota somente itens do tipo tarefa. */
export function isTasksCalendarTaskKind(kind?: string | null): boolean {
  return !kind || kind === 'Tarefa';
}

/** Pendentes, em andamento e atrasadas permanecem visíveis. Concluídas e canceladas dependem do filtro. */
export function isTasksCalendarStatusVisible(
  status: string | null | undefined,
  showCompleted: boolean,
  showCancelled: boolean,
): boolean {
  if (status === 'Completed') return showCompleted;
  if (status === 'Cancelled') return showCancelled;
  return true;
}

export const TASKS_CALENDAR_ID = 'tasks-calendar';

export const TASKS_CALENDAR_LABEL = 'Tarefas';

export function normalizeTaskCalendarIds(calendarIds?: string[] | null): string[] {
  const ids = (calendarIds ?? []).map((id) => id.trim()).filter((id) => id.length > 0);
  return ids.length > 0 ? ids : [TASKS_CALENDAR_ID];
}

export function getCalendarColor(index: number): string {
  return CALENDAR_COLORS[index % CALENDAR_COLORS.length];
}

/** Texto escuro sobre fundo claro; branco sobre fundo escuro. */
export function getReadableTextColor(background: string): string {
  const luminance = relativeLuminance(background);
  if (luminance == null) return '#ffffff';
  return luminance > 0.55 ? '#1e293b' : '#ffffff';
}

function relativeLuminance(color: string): number | null {
  const rgb = parseHexColor(color);
  if (!rgb) return null;

  const [red, green, blue] = [rgb.r, rgb.g, rgb.b].map(linearizeChannel);
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function parseHexColor(color: string): { r: number; g: number; b: number } | null {
  const hex = color.trim().replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map((channel) => channel + channel).join('')
    : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;

  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

function linearizeChannel(channel: number): number {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  day.setDate(day.getDate() - day.getDay());
  return day;
}

export function startOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1);
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

export function addYears(date: Date, years: number): Date {
  return new Date(date.getFullYear() + years, date.getMonth(), date.getDate());
}

export function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

export function isSameDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
  );
}

export function getMonthGrid(month: Date): Date[] {
  const firstDay = startOfMonth(month);
  const start = new Date(firstDay);
  start.setDate(firstDay.getDate() - firstDay.getDay());

  const days: Date[] = [];
  for (let index = 0; index < 42; index += 1) {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    days.push(day);
  }

  return days;
}

export function formatMonthYear(date: Date): string {
  return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

export function formatDayTitle(date: Date): string {
  return date.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatWeekTitle(date: Date): string {
  const start = startOfWeek(date);
  const end = addDays(start, 6);

  const startLabel = start.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
  const endLabel = end.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'short',
    year: start.getFullYear() === end.getFullYear() ? undefined : 'numeric',
  });

  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.getDate()} – ${end.getDate()} de ${start.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`;
  }

  return `${startLabel} – ${endLabel} de ${end.getFullYear()}`;
}

export function formatYearTitle(date: Date): string {
  return String(date.getFullYear());
}

export function getWeekDays(date: Date): Date[] {
  const start = startOfWeek(date);
  return Array.from({ length: 7 }, (_, index) => addDays(start, index));
}

export function getYearMonths(year: number): Date[] {
  return Array.from({ length: 12 }, (_, index) => new Date(year, index, 1));
}

export function formatInputDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatTimeForInput(value?: string | null): string {
  if (!value) return '';
  return value.slice(0, 5);
}

export function parseTimeToMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map((part) => Number.parseInt(part, 10));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 0;
  }

  return hours * 60 + minutes;
}

export function formatHourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

export const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export const DAY_LABELS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

export function defaultWorkDays() {
  return DAY_LABELS.map((_, index) => ({
    dayOfWeek: index,
    isWorkDay: index >= 1 && index <= 5,
    workStartTime: index >= 1 && index <= 5 ? '09:00:00' : undefined,
    workEndTime: index >= 1 && index <= 5 ? '18:00:00' : undefined,
  }));
}
